import classNames from 'classnames'
import Dropdown from 'components/Dropdown'
import TabButton from 'components/TabButton'
import DrupalContext from 'contexts/DrupalContext'
import useUrlParams from 'hooks/useUrlParams'
import useWindowDimensions from 'hooks/useWindowDimensions'
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

function TabsDropdown({ tabs = [] }) {
  const { t } = useTranslation()
  const tabsAsFilters = tabs.map((tab) => ({
    ...tab,
    displayName: tab.label,
    value: tab.searcher,
    count: 1,
  }))
  const isTabGlobal = tabsAsFilters.find(
    (t) => t.selected && t.value === 'global'
  )
  const displayText = t('Show')

  return (
    <div className={classNames({ 'u-mb-200': isTabGlobal })}>
      <Dropdown
        id="searcher"
        displayName={displayText}
        filters={tabsAsFilters}
        replaceFilter="true"
      />
    </div>
  )
}

function Tabs() {
  function TabsFlaps({ tabs = [], forwardRef }) {
    useEffect(() => {
      _saveTabsFlapsWidth()
      _checkTabsFlapsOverflow()
    }, [])

    return (
      <div className="tabs tabs--search u-mt-150 u-mt-200-large-up u-pt-150 u-pt-200-large-up">
        <div className="tabs__wrapper">
          <div className="tabs__list" role="tablist" ref={forwardRef}>
            {tabs.map((props) => (
              <TabButton key={props.label} {...props} />
            ))}
          </div>
        </div>
      </div>
    )
  }

  const drupal = useContext(DrupalContext)
  const { t } = useTranslation()
  const { urlParams, replaceUrlParams } = useUrlParams()

  const exposedTabs = JSON.parse(drupal.exposed_tabs)

  // Create tabs from the exposedTabs data
  const tabsData = Object.entries(exposedTabs).map(
    ([searcher, label], index) => ({
      label: t(label),
      searcher,
      selected: index === 0, // Select the first tab
    })
  )

  const [tabs, setTabs] = useState(tabsData)

  useEffect(() => {
    setTabs(
      tabs.map((tab) => ({
        ...tab,
        selected: tab.searcher === (urlParams.searcher || 'global'),
        count: 1,
        onClick: () =>
          replaceUrlParams({
            searcher: tab.searcher,
            q: urlParams.q,
          }),
      }))
    )
  }, [urlParams.searcher, urlParams.q])

  const tabsFlapsContainerRef = useRef()
  const tabsFlapsRef = useRef()
  const [tabsFlapsWidth, setTabsFlapsWidth] = useState()
  const [showFlaps, setShowFlaps] = useState(true)
  const windowDimensions = useWindowDimensions()

  const _saveTabsFlapsWidth = () => {
    const { scrollWidth } = tabsFlapsRef.current || {}
    if (scrollWidth) setTabsFlapsWidth(scrollWidth)
  }

  const _checkTabsFlapsOverflow = () => {
    const padding = 16 // tabs__overlay = 1rem
    const containerWidth = tabsFlapsContainerRef.current?.offsetWidth - padding
    const flapsWidth = tabsFlapsWidth || tabsFlapsRef.current?.offsetWidth
    const isTabsFlapsOverflown = Boolean(flapsWidth > containerWidth)
    setShowFlaps(!isTabsFlapsOverflown)
  }

  useLayoutEffect(() => {
    _checkTabsFlapsOverflow()
  }, [windowDimensions.width])

  return (
    <div ref={tabsFlapsContainerRef}>
      {showFlaps ? (
        <TabsFlaps tabs={tabs} forwardRef={tabsFlapsRef} />
      ) : (
        <TabsDropdown tabs={tabs} />
      )}
    </div>
  )
}

export default Tabs
